import { MouseEvent, useCallback, useContext, useState } from 'react'
import { Box } from '@mui/material'
import AppRouteNames from '../../constants/route'
import { OpenIDContext } from '../../api/openid'
import * as Styled from './UserMenuStyles'
import learnBackendRequestHandler from '../../api/learnBackendRequestHandler'
import AccountBox from '../../assets/icons/AccountBox.svg'
import {
	buildUserMenuItems,
	getProfileImageUrl,
	hasRealAvatar,
	UserMenuItem,
} from './userMenuConfig'

const FEEDBACK_URL = import.meta.env.VITE_SECDIM_LEARN_FEEDBACK_REDIRECT as string

const SUPPORT_URL = import.meta.env.VITE_SECDIM_SUPPORT_URL as string

const UserMenu = () => {
	const { profile, authenticated, refresh } = useContext(OpenIDContext)
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

	const handleClick = useCallback(
		(event: MouseEvent<HTMLElement>) => {
			// Open immediately and refresh in the background instead of blocking
			// on the /userinfo round-trip: refresh() updates the OpenID context
			// when it resolves, so the open menu re-renders with the fresh
			// avatar/plan (intentional stale-then-fresh). Skip the refresh when
			// the menu is already open to avoid redundant requests on rapid or
			// double clicks.
			if (!anchorEl) {
				refresh()
			}
			setAnchorEl(event.currentTarget)
		},
		[anchorEl, refresh],
	)

	const handleClose = useCallback(() => {
		setAnchorEl(null)
	}, [])

	const handleLogout = useCallback(() => {
		sessionStorage.clear()
		learnBackendRequestHandler('logout').then(() => {
			window.location.href = AppRouteNames.logout
		})
	}, [])

	const items = buildUserMenuItems({
		profile,
		authenticated,
		onLogout: handleLogout,
		idFrontendUrl: import.meta.env.VITE_SECDIM_ID_FRONTEND,
		settingsUrl: AppRouteNames.settingsID,
		feedbackUrl: FEEDBACK_URL,
		supportUrl: SUPPORT_URL,
	})

	const renderItem = (item: UserMenuItem) => {
		switch (item.type) {
			case 'profileSummary':
				return (
					<Styled.ProfileSummary key={item.id} disableRipple>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								justifyContent: 'center',
								alignItems: 'center',
								width: '100%',
							}}
						>
							<Styled.UserNavLink href={item.profileHref}>
								{item.showAvatar ? (
									<Styled.ProfileImage src={item.image} alt={item.username} />
								) : (
									<Styled.CustomPerson src={AccountBox} alt={item.username} />
								)}
							</Styled.UserNavLink>
							{item.displayName ? (
								<Styled.ProfileName variant='h5'>{item.displayName}</Styled.ProfileName>
							) : (
								<Box sx={{ mt: 0.5 }} />
							)}
							<Styled.ProfileUsername variant='caption' sx={{ textAlign: 'center' }}>
								{item.username}
							</Styled.ProfileUsername>
							<Styled.ProfilePlan variant='caption' sx={{ textAlign: 'center' }}>
								{item.plan}
							</Styled.ProfilePlan>
						</Box>
					</Styled.ProfileSummary>
				)
			case 'link':
				return (
					<Styled.CustomMenuItem key={item.id} onClick={handleClose}>
						<Styled.RowLink href={item.href} aria-label={item.ariaLabel}>
							<Styled.RowIcon>{item.icon}</Styled.RowIcon>
							<Styled.RowLabel>{item.label}</Styled.RowLabel>
						</Styled.RowLink>
					</Styled.CustomMenuItem>
				)
			case 'action':
				return (
					<Styled.CustomMenuItem
						key={item.id}
						onClick={() => {
							item.onClick()
							handleClose()
						}}
					>
						<Styled.RowButton type='button' aria-label={item.ariaLabel}>
							<Styled.RowIcon>{item.icon}</Styled.RowIcon>
							<Styled.RowLabel>{item.label}</Styled.RowLabel>
						</Styled.RowButton>
					</Styled.CustomMenuItem>
				)
			case 'label':
				return (
					<Styled.CustomMenuItem key={item.id} disabled>
						<Styled.RowLabel>{item.label}</Styled.RowLabel>
					</Styled.CustomMenuItem>
				)
			default:
				return null
		}
	}

	return (
		<>
			<Styled.MenuButton
				aria-controls='user-menu'
				aria-haspopup='true'
				aria-label='Open account menu'
				onClick={handleClick}
				disableRipple
			>
				{hasRealAvatar(profile.image) ? (
					<Styled.TriggerAvatar
						src={getProfileImageUrl(profile.image)}
						alt={profile.username}
					/>
				) : (
					<Styled.AccountIcon color='primary' />
				)}
			</Styled.MenuButton>
			<Styled.CustomMenu
				id='user-menu'
				anchorEl={anchorEl}
				keepMounted
				open={Boolean(anchorEl)}
				onClose={handleClose}
			>
				{items.map(renderItem)}
			</Styled.CustomMenu>
		</>
	)
}

export default UserMenu
